import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { trackPageView } from '../services/ga4Analytics';

const Login = () => {
  const navigate = useNavigate();
  const { login, loading } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const { email, password } = formData;

  // Track page view on component mount
  useEffect(() => {
    trackPageView('Login Page - QuoteTier');
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields', {
        toastId: 'login-validation-error'
      });
      return;
    }

    try {
      console.log('📧 Attempting login with:', email);
      const result = await login(email, password);
      console.log('✅ Login result:', result);
      
      toast.success('Welcome back!', {
        toastId: 'login-success'
      });
      
      // Check if user has completed onboarding and trial setup
      const user = result?.user || result;
      console.log('👤 User after login:', { 
        hasUser: !!user, 
        hasProfile: !!user?.profile,
        onboardingCompleted: user?.profile?.onboarding_completed,
        trialSetupCompleted: user?.profile?.trial_setup_completed
      });
      
      if (!user?.profile?.onboarding_completed) {
        console.log('➡️ Redirecting to onboarding welcome');
        navigate('/onboarding/welcome');
      } else {
        console.log('➡️ Redirecting to dashboard');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed', {
        toastId: 'login-error'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="font-instrument text-5xl font-bold text-gray-900">QuoteTier</h1>
          <p className="font-jetbrains tracking-tight mt-2 text-sm text-gray-600">Create professional quotes with ease</p>
        </div>
        <h2 className="font-instrument mt-8 text-center text-2xl font-semibold text-gray-900">
          Sign in to your account
        </h2>
      </div>

      <div className="border border-black rounded-lg mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-8" onSubmit={handleSubmit}> {/* Increased space-y for more breathing room */}
            {/* UPDATED: Email Input Field */}
            <div className="input-group">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={handleChange}
                className="input" // Use the new simplified class
                placeholder=" " /* The space is crucial for the animation! */
              />
              <label htmlFor="email" className="font-instrument text-lg md:text-xl label"> {/* Use the new simplified class */}
                Email address
              </label>
            </div>

            {/* UPDATED: Password Input Field */}
            <div className="input-group">
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={handleChange}
                className="input" // Use the new simplified class
                placeholder=" " /* The space is crucial for the animation! */
              />
              <label htmlFor="password" className="font-instrument text-lg md:text-xl label"> {/* Use the new simplified class */}
                Password
              </label>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="font-instrument text-lg md:text-xl w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px] py-3"
              >
                {loading ? (
                  <div className="flex items-center justify-center" role="status" aria-live="polite">
                    <div className="spinner spinner-sm mr-2" aria-hidden="true"></div>
                    <span>Signing in…</span>
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
              <span className="font-jetbrains tracking-tight px-2 bg-white text-gray-500">New to QuoteTier?</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/signup"
                className="font-instrument text-lg md:text-xl w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-colors duration-200 min-h-[48px] items-center"
              >
                Create your account
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
